import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { Spinner } from "~/components/ui/spinner"
import { cn } from "~/lib/utils"

/**
 * The kit's button (canvas board 01-buttons). The primary is ink, never a hue.
 * The secondary is an ink ring with a press tint (design system rule 6).
 * Green and red fill only the two money directions and the destructive act.
 * Heights come from the Alluzo control ramp: 28, 32, 36, 40, 48.
 */
const buttonVariants = cva(
  "relative inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap border border-transparent text-base font-medium transition-[background-color,border-color,color,box-shadow] duration-(--duration-leave) ease-standard outline-none focus-visible:ring-[3px] focus-visible:ring-ring aria-invalid:border-out [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      // Each variant is the phone's own (packages/ui/src/components/button-style.ts):
      // buttonFill, buttonRing and buttonInk, state by state. A disabled button
      // takes the disabled ink of its own tone over the disabled fill of its own
      // tone, so it reads as the same button, switched off, in both schemes.
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-primary-hover disabled:bg-primary-disabled disabled:text-primary-disabled-foreground",
        // The hover and press tints are laid over the button's own surface,
        // never in place of it: both are see-through, so on an ink fill (the
        // ink bar) a tint alone let the ink through and the words, in ink,
        // went with it. Over its surface it reads the same on any ground.
        secondary:
          "border-ink-ring bg-surface text-foreground hover:bg-[image:linear-gradient(var(--color-secondary),var(--color-secondary))] active:bg-[image:linear-gradient(var(--color-primary-faded),var(--color-primary-faded))] disabled:border-primary-disabled disabled:bg-surface disabled:bg-none disabled:text-primary-disabled-foreground",
        outline:
          "border-input bg-surface text-foreground shadow-low hover:border-input-hover hover:bg-well focus-visible:border-ink-ring disabled:border-border-subtle disabled:bg-surface disabled:text-primary-disabled-foreground disabled:shadow-none",
        soft:
          "bg-secondary text-foreground hover:bg-secondary-hover disabled:bg-secondary disabled:text-gray-disabled-foreground",
        ghost:
          "bg-transparent text-foreground hover:bg-secondary disabled:bg-transparent disabled:text-gray-disabled-foreground",
        link:
          "h-auto border-0 bg-transparent px-0 text-foreground underline underline-offset-3 decoration-1 hover:decoration-2 disabled:text-gray-disabled-foreground",
        destructive:
          "bg-out text-out-foreground hover:bg-out-hover disabled:bg-out-disabled disabled:text-out-disabled-foreground",
        "destructive-outline":
          "border-out-ring bg-surface text-out-text hover:bg-out-subtle disabled:border-out-ring-disabled disabled:bg-surface disabled:text-out-disabled-foreground",
        // The other act on an ink fill (the ink bar, components/kit/ink-bar.tsx):
        // a ring in the fill's own ink, its words in that ink, a faint wash of
        // it on hover and press. The main act there is the secondary.
        "on-ink":
          "border-primary-foreground/40 bg-transparent text-primary-foreground hover:border-primary-foreground hover:bg-primary-foreground/10 active:bg-primary-foreground/15 focus-visible:ring-primary-foreground/50 disabled:border-primary-foreground/20 disabled:bg-transparent disabled:text-primary-foreground/40",
        in:
          "bg-in text-in-foreground hover:bg-in-hover disabled:bg-in-disabled disabled:text-in-disabled-foreground",
        out:
          "bg-out text-out-foreground hover:bg-out-hover disabled:bg-out-disabled disabled:text-out-disabled-foreground",
      },
      // The corner rides with the size, never in the base: tailwind-merge does
      // not know rounded-control and rounded-card clash, and in the CSS
      // rounded-control comes later, so a base corner would beat the 2xl one.
      size: {
        xs: "h-7 gap-1 rounded-control px-2 text-sm [&_svg:not([class*='size-'])]:size-3.5",
        sm: "h-7 gap-1.5 rounded-control px-2.5",
        default: "h-8 rounded-control px-3",
        lg: "h-9 rounded-control px-4",
        xl: "h-10 rounded-control px-5",
        "2xl": "h-12 rounded-card px-6 text-lg",
        "icon-xs": "size-7 rounded-control [&_svg:not([class*='size-'])]:size-3.5",
        "icon-sm": "size-7 rounded-control",
        icon: "size-8 rounded-control",
        "icon-lg": "size-9 rounded-control",
        "icon-xl": "size-10 rounded-control",
        "icon-2xl": "size-12 rounded-card",
      },
    },
    compoundVariants: [
      // A link is words, not a box (kit .btn-link: no padding, no height),
      // whatever size it was given.
      { variant: "link", className: "h-auto px-0" },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

type ButtonProps = React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    /**
     * Busy: the button keeps its size, stops answering and is drawn disabled,
     * with its spinner in ink (colour rule 7: resolve the state, then the colour).
     */
    loading?: boolean
  }

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  loading = false,
  disabled,
  children,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot.Root : "button"
  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      data-loading={loading || undefined}
      aria-busy={loading || undefined}
      disabled={loading ? true : disabled}
      className={cn(buttonVariants({ variant, size, className }), "disabled:cursor-not-allowed")}
      {...props}
    >
      {/*
        A slotted child must stay the only child, so it never gets a spinner.
        Otherwise the label stays in place but unseen, and the spinner sits
        over it, so a busy button keeps its width (board 01-buttons).
      */}
      {asChild || !loading ? (
        children
      ) : (
        <>
          <span className="invisible inline-flex items-center gap-[inherit]">{children}</span>
          <Spinner
            role={undefined}
            aria-label={undefined}
            aria-hidden
            className="absolute inset-0 m-auto text-foreground"
          />
        </>
      )}
    </Comp>
  )
}

export { Button, buttonVariants, type ButtonProps }
